// src/components/Schema/SchemaComponents.tsx
import React from 'react';

const BASE_URL = 'https://www.filefusion.app';

/**
 * OrganizationSchema Component
 */
export const OrganizationSchema: React.FC = () => {
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "FileFusion",
        "alternateName": "FileFusion Digital Tools",
        "url": BASE_URL,
        "logo": {
            "@type": "ImageObject",
            "url": `${BASE_URL}/logo512.png`,
            "width": 512,
            "height": 512
        },
        "description": "FileFusion is a comprehensive digital toolkit platform offering free online tools for file management, text comparison, QR code generation, OCR, background removal, and more professional digital solutions.",
        "foundingDate": "2024",
        "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "availableLanguage": ["English"],
            "url": `${BASE_URL}/contact`
        },
        "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
        },
        "knowsAbout": [
            "File Compression",
            "Text Comparison",
            "QR Code Generation",
            "Barcode Scanning",
            "OCR Technology",
            "Background Removal",
            "Digital Tools",
            "Productivity Software"
        ],
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Digital Tools & Services",
            "itemListElement": [
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "File Compression Tool",
                        "description": "Free online file compression service"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Text Comparison Tool",
                        "description": "Free online text and code comparison service"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "QR Code Generator",
                        "description": "Free custom QR code generation service"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "OCR Tool",
                        "description": "Free text extraction from images and PDFs"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Background Remover",
                        "description": "Free AI-powered background removal service"
                    }
                }
            ]
        }
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(organizationSchema)}
        </script>
    );
};

interface Breadcrumb {
    name: string;
    url: string;
}

interface BreadcrumbSchemaProps {
    breadcrumbs: Breadcrumb[];
}

/**
 * BreadcrumbSchema Component
 */
export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({ breadcrumbs }) => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.name,
            "item": crumb.url
        }))
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
        </script>
    );
};

interface ToolSchemaProps {
    name: string;
    description: string;
    url: string;
    image?: string;
    applicationCategory?: string;
    operatingSystem?: string;
    offers?: any;
    aggregateRating?: {
        ratingValue: string | number;
        reviewCount: string | number;
    } | null;
    featureList?: string[];
}

/**
 * ToolSchema Component
 */
export const ToolSchema: React.FC<ToolSchemaProps> = ({
    name,
    description,
    url,
    image = '/og-image.png',
    applicationCategory = 'UtilitiesApplication',
    operatingSystem = 'Web Browser',
    offers = null,
    aggregateRating = null,
    featureList = []
}) => {
    const webAppSchema: any = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": name,
        "description": description,
        "url": url.startsWith('http') ? url : `${BASE_URL}${url}`,
        "applicationCategory": applicationCategory,
        "operatingSystem": operatingSystem,
        "browserRequirements": "Requires JavaScript. Requires HTML5.",
        "softwareVersion": "1.0",
        "image": image.startsWith('http') ? image : `${BASE_URL}${image}`,
        "publisher": {
            "@type": "Organization",
            "name": "FileFusion",
            "url": BASE_URL,
            "logo": {
                "@type": "ImageObject",
                "url": `${BASE_URL}/logo512.png`,
                "width": 512,
                "height": 512
            }
        },
        "inLanguage": "en-US",
        "isAccessibleForFree": true,
        "availableOnDevice": ["Desktop", "Mobile", "Tablet"]
    };

    if (offers) {
        webAppSchema.offers = {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
        };
    }

    if (aggregateRating) {
        webAppSchema.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": aggregateRating.ratingValue,
            "reviewCount": aggregateRating.reviewCount,
            "bestRating": "5",
            "worstRating": "1"
        };
    }

    if (featureList && featureList.length > 0) {
        webAppSchema.featureList = featureList;
    }

    const softwareSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": name,
        "description": description,
        "url": url.startsWith('http') ? url : `${BASE_URL}${url}`,
        "applicationCategory": applicationCategory,
        "operatingSystem": "Cross-platform",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "softwareVersion": "1.0",
        "author": {
            "@type": "Organization",
            "name": "FileFusion"
        }
    };

    return (
        <>
            <script type="application/ld+json">
                {JSON.stringify(webAppSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(softwareSchema)}
            </script>
        </>
    );
};

interface HowToStep {
    name?: string;
    text: string;
    url?: string;
    image?: string;
}

interface HowToSchemaProps {
    name: string;
    description: string;
    image?: string;
    totalTime?: string;
    estimatedCost?: { value: number; currency: string };
    tool?: string[];
    supply?: string[];
    steps: HowToStep[];
}

/**
 * HowToSchema Component
 */
export const HowToSchema: React.FC<HowToSchemaProps> = ({
    name,
    description,
    image,
    totalTime,
    estimatedCost = { value: 0, currency: 'USD' },
    tool = [],
    supply = [],
    steps = []
}) => {
    const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": name,
        "description": description,
        "image": image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : undefined,
        "totalTime": totalTime || "PT5M",
        "estimatedCost": {
            "@type": "MonetaryAmount",
            "currency": estimatedCost.currency,
            "value": estimatedCost.value
        },
        "tool": tool.map(t => ({
            "@type": "HowToTool",
            "name": t
        })),
        "supply": supply.map(s => ({
            "@type": "HowToSupply",
            "name": s
        })),
        "step": steps.map((step, index) => ({
            "@type": "HowToStep",
            "position": index + 1,
            "name": step.name || `Step ${index + 1}`,
            "text": step.text,
            "url": step.url ? (step.url.startsWith('http') ? step.url : `${BASE_URL}${step.url}`) : undefined,
            "image": step.image ? (step.image.startsWith('http') ? step.image : `${BASE_URL}${step.image}`) : undefined
        }))
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(howToSchema)}
        </script>
    );
};

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSchemaProps {
    faqs: FAQItem[];
}

/**
 * FAQSchema Component
 */
export const FAQSchema: React.FC<FAQSchemaProps> = ({ faqs }) => {
    if (!faqs || faqs.length === 0) return null;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
        </script>
    );
};

interface ArticleSchemaProps {
    headline: string;
    description: string;
    image?: string;
    datePublished: string;
    dateModified?: string;
    author?: string;
    url: string;
    keywords?: string[];
    articleSection?: string;
    wordCount?: number;
}

/**
 * ArticleSchema Component
 */
export const ArticleSchema: React.FC<ArticleSchemaProps> = ({
    headline,
    description,
    image,
    datePublished,
    dateModified,
    author = 'FileFusion Editorial Team',
    url,
    keywords = [],
    articleSection = 'Technology',
    wordCount
}) => {
    const articleSchema: any = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": headline,
        "description": description,
        "image": image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : `${BASE_URL}/og-image.png`,
        "datePublished": datePublished,
        "dateModified": dateModified || datePublished,
        "author": {
            "@type": "Organization",
            "name": author,
            "url": BASE_URL
        },
        "publisher": {
            "@type": "Organization",
            "name": "FileFusion",
            "url": BASE_URL,
            "logo": {
                "@type": "ImageObject",
                "url": `${BASE_URL}/logo512.png`,
                "width": 512,
                "height": 512
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": url.startsWith('http') ? url : `${BASE_URL}${url}`
        },
        "articleSection": articleSection,
        "keywords": keywords.join(', '),
        "inLanguage": "en-US",
        "isAccessibleForFree": true
    };

    if (wordCount) articleSchema.wordCount = wordCount;

    const blogPostingSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": headline,
        "description": description,
        "image": image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : `${BASE_URL}/og-image.png`,
        "datePublished": datePublished,
        "dateModified": dateModified || datePublished,
        "author": {
            "@type": "Organization",
            "name": author
        },
        "publisher": {
            "@type": "Organization",
            "name": "FileFusion",
            "logo": {
                "@type": "ImageObject",
                "url": `${BASE_URL}/logo512.png`
            }
        },
        "mainEntityOfPage": url.startsWith('http') ? url : `${BASE_URL}${url}`
    };

    return (
        <>
            <script type="application/ld+json">
                {JSON.stringify(articleSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(blogPostingSchema)}
            </script>
        </>
    );
};

/**
 * WebsiteSchema Component
 */
export const WebsiteSchema: React.FC = () => {
    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "FileFusion",
        "alternateName": "FileFusion Digital Tools",
        "url": BASE_URL,
        "description": "Free online digital tools for file management, text comparison, QR codes, OCR, and more professional utilities.",
        "inLanguage": "en-US",
        "publisher": {
            "@type": "Organization",
            "name": "FileFusion",
            "url": BASE_URL,
            "logo": {
                "@type": "ImageObject",
                "url": `${BASE_URL}/logo512.png`,
                "width": 512,
                "height": 512
            }
        },
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${BASE_URL}/tools?search={search_term_string}`
            },
            "query-input": "required name=search_term_string"
        }
    };

    return (
        <script type="application/ld+json">
            {JSON.stringify(websiteSchema)}
        </script>
    );
};
